import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Edit2, MessageCircle, Phone, Mail, MapPin, Car, FileText } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Avatar, AvatarFallback } from '../components/ui/Avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { StatusBadge } from '../components/service-orders/StatusBadge';
import { mockClients, mockVehicles, mockServiceOrders } from '../data/mockData';
import { formatPhone, formatDate, formatCurrency, formatPlate } from '../lib/utils';
export function ClientDetail() {
  const {
    id
  } = useParams();
  const client = mockClients.find(c => c.id === id);
  if (!client) {
    return <div className="flex h-full items-center justify-center flex-col gap-4" data-id="element-250">
        <h2 className="text-2xl font-bold" data-id="element-251">Cliente não encontrado</h2>
        <Button asChild data-id="element-252">
          <Link to="/app/clientes" data-id="element-253">Voltar para Clientes</Link>
        </Button>
      </div>;
  }
  const clientVehicles = mockVehicles.filter(v => v.clientId === client.id);
  const clientOrders = mockServiceOrders.filter(os => os.clientId === client.id).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const totalSpent = clientOrders.reduce((acc, os) => acc + os.total, 0);
  const lastVisit = clientOrders.length > 0 ? formatDate(clientOrders[0].createdAt) : '—';
  const initials = client.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const isCompany = client.document.length > 14;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="h-full flex flex-col space-y-6 pb-8" data-id="element-254">
      {/* Top Bar */}
      <div className="flex items-center gap-4" data-id="element-255">
        <Button variant="ghost" size="icon" asChild className="rounded-full" data-id="element-256">
          <Link to="/app/clientes" data-id="element-257">
            <ArrowLeft className="h-5 w-5" data-id="element-258" />
          </Link>
        </Button>
        <h1 className="text-2xl font-heading font-bold tracking-tight" data-id="element-259">
          Perfil do Cliente
        </h1>
      </div>

      {/* Hero Card */}
      <Card className="overflow-hidden border-none shadow-lg bg-card/50 backdrop-blur-sm" data-id="element-260">
        <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-261">
          <div className="flex items-center gap-6" data-id="element-262">
            <Avatar className="h-20 w-20 border-4 border-background shadow-sm" data-id="element-263">
              <AvatarFallback className="bg-primary/10 text-primary text-2xl font-medium" data-id="element-264">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div data-id="element-265">
              <h1 className="text-3xl font-heading font-bold tracking-tight mb-1" data-id="element-266">
                {client.name}
              </h1>
              <div className="flex items-center gap-3 text-muted-foreground" data-id="element-267">
                <span className="font-mono text-sm" data-id="element-268">{client.document}</span>
                <span data-id="element-269">•</span>
                <span className="text-sm" data-id="element-270">
                  Cliente desde {formatDate(client.createdAt)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-271">
            <Button variant="outline" className="gap-2" data-id="element-272">
              <MessageCircle className="h-4 w-4 text-emerald-500" data-id="element-273" /> WhatsApp
            </Button>
            <Button variant="outline" className="gap-2" data-id="element-274">
              <Edit2 className="h-4 w-4" data-id="element-275" /> Editar
            </Button>
            <Button className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-id="element-276">
              <Plus className="h-4 w-4" data-id="element-277" /> Nova OS
            </Button>
          </div>
        </div>
      </Card>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-278">
        <Card data-id="element-279">
          <CardContent className="p-6" data-id="element-280">
            <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-281">
              Total de OS
            </p>
            <p className="text-2xl font-bold" data-id="element-282">{clientOrders.length}</p>
          </CardContent>
        </Card>
        <Card data-id="element-283">
          <CardContent className="p-6" data-id="element-284">
            <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-285">
              Total Gasto
            </p>
            <p className="text-2xl font-bold text-primary" data-id="element-286">
              {formatCurrency(totalSpent)}
            </p>
          </CardContent>
        </Card>
        <Card data-id="element-287">
          <CardContent className="p-6" data-id="element-288">
            <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-289">
              Veículos
            </p>
            <p className="text-2xl font-bold" data-id="element-290">{clientVehicles.length}</p>
          </CardContent>
        </Card>
        <Card data-id="element-291">
          <CardContent className="p-6" data-id="element-292">
            <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-293">
              Última Visita
            </p>
            <p className="text-2xl font-bold" data-id="element-294">{lastVisit}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-295">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-296">
          {/* Veículos Card */}
          <Card data-id="element-297">
            <CardHeader className="flex flex-row items-center justify-between pb-2" data-id="element-298">
              <CardTitle className="flex items-center gap-2" data-id="element-299">
                <Car className="h-5 w-5 text-primary" data-id="element-300" /> Veículos vinculados
              </CardTitle>
              <Button variant="ghost" size="sm" className="gap-1 text-primary hover:text-primary hover:bg-primary/10" data-id="element-301">
                <Plus className="h-4 w-4" data-id="element-302" /> Adicionar
              </Button>
            </CardHeader>
            <CardContent data-id="element-303">
              {clientVehicles.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-304">
                  {clientVehicles.map(v => <Link key={v.id} to={`/app/veiculos/${v.id}`} data-id="element-305">
                      <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-muted/30 hover:border-primary/50 transition-colors cursor-pointer" data-id="element-306">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0" data-id="element-307">
                          <Car className="h-5 w-5 text-primary" data-id="element-308" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-309">
                          <p className="font-semibold truncate" data-id="element-310">
                            {v.brand} {v.model}
                          </p>
                          <p className="text-xs text-muted-foreground truncate" data-id="element-311">
                            {v.year} • {v.engine}
                          </p>
                        </div>
                        <Badge variant="secondary" className="font-mono" data-id="element-312">
                          {formatPlate(v.plate)}
                        </Badge>
                      </div>
                    </Link>)}
                </div> : <div className="text-center py-8 text-muted-foreground" data-id="element-313">
                  Nenhum veículo cadastrado para este cliente.
                </div>}
            </CardContent>
          </Card>

          {/* Histórico de OS */}
          <Card data-id="element-314">
            <CardHeader data-id="element-315">
              <CardTitle className="flex items-center gap-2" data-id="element-316">
                <FileText className="h-5 w-5 text-primary" data-id="element-317" /> Histórico de
                Ordens de Serviço
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0" data-id="element-318">
              {clientOrders.length > 0 ? <Table data-id="element-319">
                  <TableHeader data-id="element-320">
                    <TableRow data-id="element-321">
                      <TableHead className="pl-6" data-id="element-322">OS</TableHead>
                      <TableHead data-id="element-323">Veículo</TableHead>
                      <TableHead data-id="element-324">Status</TableHead>
                      <TableHead data-id="element-325">Data</TableHead>
                      <TableHead className="text-right pr-6" data-id="element-326">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-327">
                    {clientOrders.map(os => {
                  const v = clientVehicles.find(veh => veh.id === os.vehicleId);
                  return <TableRow key={os.id} className="cursor-pointer hover:bg-muted/50 transition-colors" data-id="element-328">
                          <TableCell className="pl-6 font-mono font-medium" data-id="element-329">
                            <Link to={`/app/ordens/${os.id}`} className="block" data-id="element-330">
                              {os.number}
                            </Link>
                          </TableCell>
                          <TableCell data-id="element-331">
                            <Link to={`/app/ordens/${os.id}`} className="block" data-id="element-332">
                              {v?.model || 'Desconhecido'}
                            </Link>
                          </TableCell>
                          <TableCell data-id="element-333">
                            <Link to={`/app/ordens/${os.id}`} className="block" data-id="element-334">
                              <StatusBadge status={os.status} data-id="element-335" />
                            </Link>
                          </TableCell>
                          <TableCell data-id="element-336">
                            <Link to={`/app/ordens/${os.id}`} className="block" data-id="element-337">
                              {formatDate(os.createdAt)}
                            </Link>
                          </TableCell>
                          <TableCell className="text-right pr-6 font-medium" data-id="element-338">
                            <Link to={`/app/ordens/${os.id}`} className="block" data-id="element-339">
                              {formatCurrency(os.total)}
                            </Link>
                          </TableCell>
                        </TableRow>;
                })}
                  </TableBody>
                </Table> : <div className="text-center py-12 text-muted-foreground" data-id="element-340">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" data-id="element-341" />
                  <p data-id="element-342">Nenhuma OS registrada ainda.</p>
                </div>}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6" data-id="element-343">
          {/* Informações de Contato */}
          <Card data-id="element-344">
            <CardHeader data-id="element-345">
              <CardTitle data-id="element-346">Informações de Contato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-347">
              <div className="flex items-start gap-3" data-id="element-348">
                <Phone className="h-4 w-4 text-muted-foreground mt-0.5" data-id="element-349" />
                <div data-id="element-350">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1" data-id="element-351">
                    Telefone
                  </p>
                  <p className="text-sm" data-id="element-352">{formatPhone(client.phone)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-353">
                <MessageCircle className="h-4 w-4 text-muted-foreground mt-0.5" data-id="element-354" />
                <div data-id="element-355">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1" data-id="element-356">
                    WhatsApp
                  </p>
                  <p className="text-sm flex items-center gap-2" data-id="element-357">
                    {formatPhone(client.whatsapp)}
                    <span className="h-2 w-2 rounded-full bg-emerald-500" title="Disponível no WhatsApp" data-id="element-358" />
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-359">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5" data-id="element-360" />
                <div data-id="element-361">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1" data-id="element-362">
                    E-mail
                  </p>
                  <p className="text-sm" data-id="element-363">{client.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-364">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" data-id="element-365" />
                <div data-id="element-366">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1" data-id="element-367">
                    Endereço
                  </p>
                  <p className="text-sm leading-relaxed" data-id="element-368">{client.address}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card data-id="element-369">
            <CardHeader data-id="element-370">
              <CardTitle data-id="element-371">Tags</CardTitle>
            </CardHeader>
            <CardContent data-id="element-372">
              <div className="flex flex-wrap gap-2" data-id="element-373">
                <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20" data-id="element-374">
                  Cliente VIP
                </Badge>
                {isCompany && <Badge className="bg-automotive-orange/10 text-automotive-orange hover:bg-automotive-orange/20 border-automotive-orange/20" data-id="element-375">
                    Frota corporativa
                  </Badge>}
                <Badge className="bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 border-emerald-500/20" data-id="element-376">
                  Pontual
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Observações */}
          <Card data-id="element-377">
            <CardHeader data-id="element-378">
              <CardTitle data-id="element-379">Observações</CardTitle>
            </CardHeader>
            <CardContent data-id="element-380">
              <textarea className="w-full min-h-[120px] p-3 rounded-lg border border-border bg-muted/30 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Adicione notas internas sobre este cliente..." defaultValue="Prefere atendimento pela manhã. Sempre paga em dia." data-id="element-381" />
            </CardContent>
          </Card>
        </div>
      </div>
    </motion.div>;
}